import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { endpoint } from '../../../../../pages';
import { IProject } from '../../../../../api/IApiResponse';
import { StepItem } from '../types';

export interface UseStepperStateProps {
    dynamicProject: IProject | null | undefined;
}

export interface UseStepperStateReturn {
    steps: StepItem[];
    computedActiveStep: number;
    currentPath: string;
    isApiLoading: boolean;
    setIsApiLoading: (loading: boolean) => void;
    errorDialogContent: React.ReactNode | null;
    setErrorDialogContent: (content: React.ReactNode | null) => void;
    showErrorDialog: (message: string) => void;
}

/**
 * Hook for managing stepper state and step configuration
 * Handles step definitions, active step calculation, and error dialogs
 */
export const useStepperState = ({ dynamicProject }: UseStepperStateProps): UseStepperStateReturn => {
    const { t } = useTranslation();
    const location = useLocation();

    // Local state for API loading and error handling
    const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
    const [errorDialogContent, setErrorDialogContent] = useState<React.ReactNode | null>(null);

    // Current path for step calculation
    const currentPath = location.pathname.split('/')[1];

    /**
     * Memoized steps configuration based on project state and translations
     */
    const steps = React.useMemo(() => {
        const baseSteps: StepItem[] = [
            {
                label: t('breadcrumb_project_info'),
                endpoint: endpoint.projectInfo,
                id: 'project_info',
            },
            {
                label: t('breadcrumb_project_type'),
                endpoint: endpoint.projectType,
                id: 'project_type',
            },
            {
                label: t('breadcrumb_design_condition'),
                endpoint: endpoint.designCondition,
                id: 'design_condition',
            },
            {
                label: t('breadcrumb_results'),
                endpoint: endpoint.results,
                id: 'results',
                isRunStep: true,
                hasBeenRunFlag: dynamicProject?.hasBeenRan,
            },
            {
                label: t('breadcrumb_geogrid'),
                endpoint: endpoint.geogrid,
                id: 'geogrid_design',
            },
            {
                label: dynamicProject && dynamicProject.geogridHasBeenRan ? t('breadcrumb_geogrid_results') : t('breadcrumb_geogrid_results_run'),
                endpoint: endpoint.geogridResults,
                id: 'geogrid_results',
                isRunStep: true,
                hasBeenRunFlag: dynamicProject?.geogridHasBeenRan,
            },
        ];

        return baseSteps;
    }, [t, dynamicProject]);

    /**
     * Compute active step based on current path and step configuration
     */
    const computedActiveStep = React.useMemo(() => {
        const idx = steps.findIndex(step => step.endpoint.includes(currentPath));
        return idx !== -1 ? idx : 0;
    }, [steps, currentPath]);

    /**
     * Utility function for showing error dialogs
     */
    const showErrorDialog = useCallback(
        (message: string) => {
            setErrorDialogContent(React.createElement('span', null, message));
            setIsApiLoading(false);
        },
        [setErrorDialogContent, setIsApiLoading]
    );

    return {
        steps,
        computedActiveStep,
        currentPath,
        isApiLoading,
        setIsApiLoading,
        errorDialogContent,
        setErrorDialogContent,
        showErrorDialog,
    };
};
